<template>
  <div v-if="isVisible" class="filter-overlay">
    <div class="filter-overlay-backdrop" @click="close"></div>
    <div class="filter-overlay-content">
      <div class="filter-header">
        <h2>Import Metering Points</h2>
      </div>

      <!-- Carousel Navigation -->
      <div class="carousel-navigation">
        <div
            v-for="(step, index) in steps"
            :key="index"
            :class="['step-indicator', { active: currentStep === index, completed: currentStep > index }]"
        >
          <div class="step-number">{{ index + 1 }}</div>
          <div class="step-name">{{ step }}</div>
        </div>
      </div>

      <!-- Form Steps -->
      <div class="form-steps">
        <!-- Step 0: Select Import Type -->
        <div v-if="currentStep === 0" class="form-step">
          <div class="form-group">
            <div class="filter-label-container">
              <label class="filter-label">Import Method</label>
            </div>
            <div class="toggle-selection-wrapper">
              <button
                  :class="['toggle-option', { active: importMethod === 'file' }]"
                  @click="importMethod = 'file'"
              >
                File Upload
              </button>
              <button
                  :class="['toggle-option', { active: importMethod === 'manual' }]"
                  @click="importMethod = 'manual'"
              >
                Manual Input
              </button>
            </div>
          </div>
        </div>

        <!-- Step 1: Manual Input form (if manual input selected) -->
        <div v-if="currentStep === 1 && importMethod === 'manual'" class="form-step">
          <div class="manual-entry-form">
            <div class="filter-label-container">
              <label class="filter-label">Enter Metering Point Details</label>
            </div>
            <div v-for="(entry, index) in manualEntries" :key="index" class="manual-entry-row">
              <div class="entry-fields">
                <input type="text" v-model="entry.mpid" placeholder="MPID *" required />
                <input type="text" v-model="entry.street" placeholder="Street *" required />
                <input type="text" v-model="entry.city" placeholder="City *" required />
                <input type="text" v-model="entry.customerFName" placeholder="Customer *" required />
              </div>
              <button v-if="index > 0" class="remove-entry-button" @click="removeEntry(index)">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <button class="add-entry-button" @click="addEntry">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              Add Another Entry
            </button>
          </div>
        </div>

        <!-- Step 1: CSV Delimiter and Device Attachment (for file imports) -->
        <div v-if="currentStep === 1 && importMethod === 'file'" class="form-step">
          <div class="form-group">
            <div class="filter-label-container">
              <label class="filter-label">CSV Delimiter</label>
            </div>
            <div class="toggle-selection-wrapper">
              <button
                  :class="['toggle-option', { active: delimiter === ',' }]"
                  @click="delimiter = ','"
              >
                Comma (,)
              </button>
              <button
                  :class="['toggle-option', { active: delimiter === ';' }]"
                  @click="delimiter = ';'"
              >
                Semicolon (;)
              </button>
              <button
                  :class="['toggle-option', { active: delimiter === '\t' }]"
                  @click="delimiter = '\t'"
              >
                Tab
              </button>
            </div>
          </div>

          <!-- Device Attachment Toggle -->
          <div class="form-group" style="margin-top: 20px;">
            <div class="filter-label-container">
              <label class="filter-label">Device Attachment</label>
              <p class="filter-description">
                Enable this option to automatically attach metering points to devices using the LogicalDeviceNumber column in your CSV.
              </p>
            </div>
            <div class="checkbox-wrapper">
              <label class="checkbox-label">
                <input
                    type="checkbox"
                    v-model="attachToDevice"
                    class="checkbox-input"
                />
                <span class="checkbox-text">Automatically attach to devices</span>
              </label>
            </div>
            <div v-if="attachToDevice" class="info-message">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
              <span>Make sure your CSV includes a "LogicalDeviceNumber" column with valid device LDNs.</span>
            </div>
          </div>

          <div class="form-group" style="margin-top: 20px;">
            <button class="action-button" @click="downloadTemplate">
              Download CSV Template
            </button>
          </div>
        </div>

        <!-- Step 2: File Upload (for file imports) -->
        <div v-if="currentStep === 2 && importMethod === 'file'" class="form-step">
          <div class="form-group">
            <div class="filter-label-container">
              <label class="filter-label">Upload File</label>
            </div>
            <div class="file-drop-area"
                 @dragover.prevent="onDragOver"
                 @dragleave.prevent="onDragLeave"
                 @drop.prevent="onDrop"
                 :class="{ 'drag-over': isDragOver }"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="17 8 12 3 7 8"></polyline>
                <line x1="12" y1="3" x2="12" y2="15"></line>
              </svg>
              <p>Drag & drop your CSV file here</p>
              <p class="or-text">OR</p>
              <label class="file-select-button">
                Browse Files
                <input type="file"
                       accept=".csv"
                       @change="onFileSelected"
                       ref="fileInput" />
              </label>
            </div>

            <div v-if="selectedFile" class="selected-file-info">
              <div class="file-name">{{ selectedFile.name }}</div>
              <button class="remove-file-button" @click="removeFile">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
          </div>
        </div>

        <!-- Step 3: Preview Data (for file imports) -->
        <div v-if="currentStep === 3 && importMethod === 'file' && showPreview && importPreview.length > 0" class="form-step">
          <div class="file-preview">
            <div class="filter-label-container">
              <label class="filter-label">Data Preview ({{ importPreview.length }} records)</label>
              <p v-if="attachToDevice && hasDeviceColumn" class="filter-description success">
                ✓ LogicalDeviceNumber column detected - devices will be attached automatically
              </p>
              <p v-else-if="attachToDevice && !hasDeviceColumn" class="filter-description warning">
                ⚠ LogicalDeviceNumber column not found - metering points will be created without device attachment
              </p>
            </div>
            <table class="preview-table">
              <thead>
              <tr>
                <th v-for="(header, index) in previewHeaders" :key="index">
                  {{ header }}
                  <span v-if="header === 'LogicalDeviceNumber' && attachToDevice" class="column-badge">Device</span>
                </th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(row, rowIndex) in previewData" :key="rowIndex">
                <td v-for="(cell, cellIndex) in row" :key="cellIndex">{{ cell }}</td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Confirmation Step for Manual Entry -->
        <div v-if="currentStep === 2 && importMethod === 'manual'" class="form-step">
          <div class="confirmation-title">Review Metering Points</div>
          <div class="confirmation-list">
            <div v-for="(entry, index) in validManualEntries" :key="index" class="confirmation-item">
              <span>{{ index + 1 }}. {{ entry.mpid }} - {{ entry.street }}, {{ entry.city }} ({{ entry.customerFName }})</span>
            </div>
            <div v-if="validManualEntries.length === 0" class="empty-message">
              No valid metering points to import. Please go back and add at least one valid entry.
            </div>
          </div>
        </div>
      </div>

      <!-- Navigation Buttons -->
      <div class="filter-actions">
        <button v-if="currentStep > 0" class="action-button" @click="previousStep">
          Back
        </button>
        <button class="action-button" @click="close">Cancel</button>
        <button
            v-if="!isLastStep"
            class="action-button"
            @click="nextStep"
            :disabled="!canProceed"
        >
          Next
        </button>
        <button
            v-else
            class="action-button submit-button"
            @click="importData"
            :disabled="isImporting || !canImport"
        >
          {{ isImporting ? 'Importing...' : 'Import Metering Points' }}
        </button>
      </div>

      <!-- Import Results Modal -->
      <div v-if="showResults" class="results-modal">
        <div class="results-content">
          <h3>Import Results</h3>
          <div class="results-summary">
            <div class="result-item success">
              <strong>{{ importResults.imported || 0 }}</strong> metering points imported
            </div>
            <div v-if="importResults.attached > 0" class="result-item success">
              <strong>{{ importResults.attached }}</strong> devices attached
            </div>
          </div>

          <div v-if="importResults.warnings && importResults.warnings.length > 0" class="warnings-section">
            <h4>Warnings ({{ importResults.warnings.length }})</h4>
            <div class="warning-list">
              <div v-for="(warning, index) in importResults.warnings" :key="index" class="warning-item">
                {{ warning }}
              </div>
            </div>
          </div>

          <div v-if="importResults.errors && importResults.errors.length > 0" class="errors-section">
            <h4>Errors ({{ importResults.errors.length }})</h4>
            <div class="error-list">
              <div v-for="(error, index) in importResults.errors" :key="index" class="error-item">
                {{ error }}
              </div>
            </div>
          </div>

          <button class="action-button" @click="closeResults">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MpidImportService from "@/services/MpidImportService";

export default {
  name: 'ImportMpidOverlay',
  props: {
    isVisible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      currentStep: 0,
      importMethod: 'file',
      importType: 'csv',
      delimiter: ',',
      attachToDevice: false,
      selectedFile: null,
      isDragOver: false,
      showPreview: false,
      importPreview: [],
      previewHeaders: [],
      previewData: [],
      isImporting: false,
      showResults: false,
      importResults: {},
      manualEntries: [
        { mpid: '', street: '', city: '', zipCode: '', customerFName: '', country: '' }
      ]
    };
  },
  computed: {
    steps() {
      return this.importMethod === 'file'
          ? ['Import Method', 'CSV Options', 'Upload', 'Preview']
          : ['Import Method', 'Manual Entry', 'Review'];
    },
    isLastStep() {
      return this.currentStep === this.steps.length - 1;
    },
    canProceed() {
      // Step 0: Import Method selection
      if (this.currentStep === 0) return true;

      // Step 1: CSV Options or Manual Entry
      if (this.currentStep === 1) {
        // File import - delimiter is pre-selected
        if (this.importMethod === 'file') return true;

        // Manual entry - at least one valid entry
        if (this.importMethod === 'manual') {
          return this.manualEntries.some(entry =>
              entry.mpid && entry.street && entry.city && entry.customerFName
          );
        }
      }

      // Step 2: File Upload
      if (this.currentStep === 2 && this.importMethod === 'file') {
        return !!this.selectedFile;
      }

      return true;
    },
    canImport() {
      return this.importMethod === 'file'
          ? !!this.selectedFile
          : this.validManualEntries.length > 0;
    },
    validManualEntries() {
      return this.manualEntries.filter(entry =>
          entry.mpid && entry.street && entry.city && entry.customerFName
      );
    },
    hasDeviceColumn() {
      return this.previewHeaders.includes('LogicalDeviceNumber');
    }
  },
  watch: {
    isVisible(newVal) {
      if (newVal) this.resetForm();
    }
  },
  methods: {
    close() {
      if (!this.isImporting) {
        this.$emit('close');
      }
    },
    resetForm() {
      this.currentStep = 0;
      this.importMethod = 'file';
      this.importType = 'csv';
      this.delimiter = ',';
      this.attachToDevice = false;
      this.selectedFile = null;
      this.isDragOver = false;
      this.showPreview = false;
      this.importPreview = [];
      this.previewHeaders = [];
      this.previewData = [];
      this.isImporting = false;
      this.showResults = false;
      this.importResults = {};
      this.manualEntries = [
        { mpid: '', street: '', city: '', zipCode: '', customerFName: '', country: '' }
      ];
    },
    nextStep() {
      if (this.canProceed) {
        // Process file when reaching the upload step
        if (this.importMethod === 'file' && this.currentStep === 2 && this.selectedFile) {
          this.processFile(this.selectedFile);
        }
        this.currentStep++;
      }
    },
    previousStep() {
      if (this.currentStep > 0) {
        this.currentStep--;
      }
    },
    onDragOver() {
      this.isDragOver = true;
    },
    onDragLeave() {
      this.isDragOver = false;
    },
    onDrop(event) {
      this.isDragOver = false;
      const files = event.dataTransfer.files;
      if (files.length) {
        this.selectedFile = files[0];
      }
    },
    onFileSelected(event) {
      const files = event.target.files;
      if (files.length) {
        this.selectedFile = files[0];
      }
    },
    processFile(file) {
      // Validate file extension
      const fileExtension = file.name.split('.').pop().toLowerCase();
      if (fileExtension !== 'csv') {
        alert('Please upload a valid CSV file.');
        this.removeFile();
        return;
      }

      // Parse CSV file
      MpidImportService.parseCsv(file, this.delimiter)
          .then(data => {
            this.importPreview = data;

            // Set up preview data for display
            if (data.length > 0) {
              this.previewHeaders = Object.keys(data[0]);
              this.previewData = data.slice(0, 5).map(item =>
                  this.previewHeaders.map(header => item[header])
              );
              this.showPreview = true;
            } else {
              alert('No data found in the CSV file.');
              this.removeFile();
            }
          })
          .catch(error => {
            console.error('Error parsing CSV:', error);
            alert('Error parsing CSV file. Please check the format and try again.');
            this.removeFile();
          });
    },
    removeFile() {
      this.selectedFile = null;
      this.showPreview = false;
      this.importPreview = [];
      this.previewHeaders = [];
      this.previewData = [];
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    addEntry() {
      this.manualEntries.push({
        mpid: '',
        street: '',
        city: '',
        zipCode: '',
        customerFName: '',
        country: ''
      });
    },
    removeEntry(index) {
      if (this.manualEntries.length > 1) {
        this.manualEntries.splice(index, 1);
      }
    },
    downloadTemplate() {
      MpidImportService.downloadTemplate(this.delimiter, this.attachToDevice);
    },
    async importData() {
      if (this.isImporting) return;
      this.isImporting = true;

      try {
        let response;

        if (this.importMethod === 'file' && this.selectedFile) {
          // Upload file with attachToDevice parameter
          response = await MpidImportService.uploadMeteringPointsFile(
              this.selectedFile,
              'csv',
              this.delimiter,
              this.attachToDevice
          );
        } else if (this.importMethod === 'manual') {
          // Format manual entries for API
          const meteringPoints = this.validManualEntries.map(entry => ({
            mpid: entry.mpid,
            street: entry.street,
            city: entry.city,
            zip_code: entry.zipCode || null,
            country: entry.country || null,
            customer_fname: entry.customerFName,
            lat: 0,
            lon: 0
          }));

          // Upload manual entries
          response = await MpidImportService.uploadMeteringPoints(meteringPoints);
        } else {
          throw new Error('No valid import method selected');
        }

        // Handle successful response
        this.importResults = response.data;
        this.showResults = true;

      } catch (error) {
        console.error('Error importing data:', error);

        // Extract and format error message
        let errorMessage = 'An error occurred during import.';

        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response?.data?.errors) {
          if (Array.isArray(error.response.data.errors)) {
            errorMessage = error.response.data.errors.join('\n');
          } else {
            errorMessage = 'Multiple errors occurred during import.';
          }
        }

        alert(`Import failed: ${errorMessage}`);
      } finally {
        this.isImporting = false;
      }
    },
    closeResults() {
      this.showResults = false;
      this.$emit('close');
      this.$emit('imported'); // Trigger refresh of metering points list
    }
  }
};
</script>

<style scoped>
/* Overlay */
.filter-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.filter-overlay-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to top, rgba(32, 32, 32, 0.1) 0%, rgba(16, 16, 16, 0.95) 100%);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(4px);
  animation: fadeIn 0.3s ease forwards;
}

.filter-overlay-content {
  position: relative;
  width: 600px;
  max-width: 90%;
  max-height: 90vh;
  border-radius: 8px;
  padding: 24px;
  color: white;
  overflow-y: auto;
  animation: slideIn 0.3s ease forwards;
  z-index: 1001;
}

/* Header */
.filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 16px;
}

.filter-header h2 {
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin: 0;
}

/* Carousel/Steps Navigation */
.carousel-navigation {
  display: flex;
  justify-content: space-between;
  margin-bottom: 32px;
  position: relative;
}

.carousel-navigation::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  z-index: 1;
}

.step-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;
}

.step-number {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
  border: 2px solid transparent;
  transition: all 0.3s ease;
}

.step-name {
  font-size: 10px;
  font-weight: 500;
  opacity: 0.7;
  text-align: center;
  transition: opacity 0.3s ease;
}

.step-indicator.active .step-number {
  background-color: rgba(79, 195, 247, 0.2);
  border-color: #4fc3f7;
  color: #4fc3f7;
}

.step-indicator.active .step-name {
  opacity: 1;
  color: #4fc3f7;
}

.step-indicator.completed .step-number {
  background-color: rgba(79, 195, 247, 0.5);
  border-color: #4fc3f7;
}

/* Form Steps */
.form-step {
  animation: fadeIn 0.3s ease forwards;
}

.form-group {
  margin-bottom: 20px;
}

.filter-label-container {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 8px;
}

.filter-label {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  opacity: 0.9;
}

.filter-description {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.4;
  letter-spacing: 0.02em;
}

.filter-description.success {
  color: #4fc3f7;
  font-weight: 500;
}

.filter-description.warning {
  color: rgba(255, 196, 0, 0.9);
  font-weight: 500;
}

/* Toggle selection style */
.toggle-selection-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 8px;
}

.toggle-option {
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  cursor: pointer;
  padding: 0;
  opacity: 0.4;
  transition: opacity 0.2s;
}

.toggle-option:hover,
.toggle-option.active {
  opacity: 1;
}

.toggle-option.active {
  position: relative;
}

.toggle-option.active::after {
  content: '';
  position: absolute;
  bottom: -5px;
  left: 0;
  width: 100%;
  height: 2px;
  background-color: #4fc3f7;
  opacity: 0.7;
}

/* Checkbox styles */
.checkbox-wrapper {
  margin-top: 12px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  padding: 8px 0;
}

.checkbox-input {
  width: 16px;
  height: 16px;
  margin: 0;
  margin-right: 12px;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  background-color: rgba(255, 255, 255, 0.05);
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 3px;
  position: relative;
  transition: all 0.2s ease;
}

.checkbox-input:hover {
  border-color: rgba(255, 255, 255, 0.4);
  background-color: rgba(255, 255, 255, 0.08);
}

.checkbox-input:checked {
  background-color: rgba(79, 195, 247, 0.2);
  border-color: #4fc3f7;
}

.checkbox-input:checked::after {
  content: '';
  position: absolute;
  top: 1px;
  left: 4px;
  width: 4px;
  height: 8px;
  border: solid #4fc3f7;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.checkbox-text {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.9);
  letter-spacing: 0.02em;
}

/* Info message box */
.info-message {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 12px;
  padding: 12px;
  background-color: rgba(79, 195, 247, 0.08);
  border-left: 2px solid rgba(79, 195, 247, 0.5);
  border-radius: 4px;
  font-size: 11px;
  color: rgba(79, 195, 247, 0.9);
  line-height: 1.5;
}

.info-message svg {
  flex-shrink: 0;
  margin-top: 1px;
  color: rgba(79, 195, 247, 0.7);
}

/* File Drop Area */
.file-drop-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  border: 2px dashed rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  margin-bottom: 20px;
  transition: all 0.2s;
}

.file-drop-area.drag-over {
  border-color: #4fc3f7;
  background-color: rgba(79, 195, 247, 0.05);
}

.file-drop-area svg {
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 16px;
}

.file-drop-area p {
  margin: 8px 0;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
}

.file-drop-area .or-text {
  color: rgba(255, 255, 255, 0.4);
  font-size: 12px;
  margin: 8px 0;
}

.file-select-button {
  background-color: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 4px;
  padding: 10px 16px;
  color: white;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
  margin-top: 8px;
  display: inline-block;
}

.file-select-button:hover {
  background-color: rgba(255, 255, 255, 0.15);
}

.file-select-button input[type="file"] {
  display: none;
}

.selected-file-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
  margin-bottom: 20px;
}

.file-name {
  font-size: 14px;
  color: white;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.remove-file-button {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
}

.remove-file-button:hover {
  color: rgba(255, 100, 100, 0.9);
}

/* Preview Table */
.file-preview {
  margin-top: 12px;
}

.preview-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  margin-top: 12px;
  background-color: rgba(255, 255, 255, 0.02);
  border-radius: 4px;
  overflow: hidden;
}

.preview-table th,
.preview-table td {
  padding: 10px 12px;
  text-align: left;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.preview-table th {
  background-color: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.8);
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.preview-table td {
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
}

.preview-table tr:last-child td {
  border-bottom: none;
}

.column-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 2px 6px;
  background-color: rgba(79, 195, 247, 0.2);
  color: #4fc3f7;
  font-size: 9px;
  border-radius: 3px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

/* Manual Entry Form */
.manual-entry-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 8px;
}

.manual-entry-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.entry-fields {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.entry-fields input {
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  padding: 10px 12px;
  color: white;
  font-size: 13px;
  transition: all 0.2s;
}

.entry-fields input:focus {
  outline: none;
  border-color: rgba(79, 195, 247, 0.5);
  background-color: rgba(255, 255, 255, 0.08);
}

.entry-fields input::placeholder {
  color: rgba(255, 255, 255, 0.3);
}

.remove-entry-button {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
}

.remove-entry-button:hover {
  color: rgba(255, 100, 100, 0.9);
}

.add-entry-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px dashed rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  padding: 12px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 8px;
}

.add-entry-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.3);
  color: rgba(255, 255, 255, 0.9);
}

/* Confirmation Section */
.confirmation-title {
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: 20px;
  opacity: 0.9;
}

.confirmation-list {
  background-color: rgba(255, 255, 255, 0.03);
  border-radius: 6px;
  padding: 16px;
  max-height: 300px;
  overflow-y: auto;
}

.confirmation-item {
  margin-bottom: 12px;
  padding: 8px 12px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
  border-left: 2px solid rgba(79, 195, 247, 0.3);
}

.confirmation-item:last-child {
  margin-bottom: 0;
}

.empty-message {
  font-size: 13px;
  color: rgba(255, 196, 0, 0.9);
  padding: 12px;
  text-align: center;
  background-color: rgba(255, 196, 0, 0.05);
  border-radius: 4px;
  border-left: 2px solid rgba(255, 196, 0, 0.5);
}

/* Results Modal */
.results-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: fadeIn 0.2s ease forwards;
}

.results-content {
  background: linear-gradient(135deg, rgba(40, 40, 40, 0.98) 0%, rgba(30, 30, 30, 0.98) 100%);
  padding: 28px;
  border-radius: 8px;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  color: white;
  animation: slideIn 0.3s ease forwards;
}

.results-content h3 {
  margin: 0 0 20px 0;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: white;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 12px;
}

.results-summary {
  margin-bottom: 24px;
}

.result-item {
  padding: 12px 16px;
  margin-bottom: 10px;
  border-radius: 4px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.result-item strong {
  font-weight: 700;
  font-size: 20px;
}

.result-item.success {
  background-color: rgba(79, 195, 247, 0.15);
  color: #4fc3f7;
  border-left: 3px solid #4fc3f7;
}

.warnings-section,
.errors-section {
  margin-top: 24px;
}

.warnings-section h4,
.errors-section h4 {
  margin: 0 0 12px 0;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(255, 255, 255, 0.8);
}

.warning-list,
.error-list {
  max-height: 250px;
  overflow-y: auto;
}

.warning-item,
.error-item {
  padding: 10px 12px;
  margin-bottom: 8px;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.5;
}

.warning-item {
  background-color: rgba(255, 196, 0, 0.1);
  color: rgba(255, 196, 0, 0.95);
  border-left: 3px solid rgba(255, 196, 0, 0.6);
}

.error-item {
  background-color: rgba(255, 100, 100, 0.1);
  color: rgba(255, 150, 150, 0.95);
  border-left: 3px solid rgba(255, 100, 100, 0.6);
}

/* Action buttons */
.filter-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  cursor: pointer;
  padding: 10px 18px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.action-button:hover:not(:disabled) {
  background-color: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.2);
}

.action-button:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.submit-button {
  background-color: rgba(79, 195, 247, 0.2);
  border-color: rgba(79, 195, 247, 0.3);
  color: #4fc3f7;
}

.submit-button:hover:not(:disabled) {
  background-color: rgba(79, 195, 247, 0.3);
  border-color: rgba(79, 195, 247, 0.5);
}

.results-content .action-button {
  margin-top: 20px;
  width: 100%;
  padding: 12px 18px;
  font-size: 12px;
}

/* Scrollbar styling */
.filter-overlay-content::-webkit-scrollbar,
.confirmation-list::-webkit-scrollbar,
.warning-list::-webkit-scrollbar,
.error-list::-webkit-scrollbar,
.results-content::-webkit-scrollbar {
  width: 6px;
}

.filter-overlay-content::-webkit-scrollbar-track,
.confirmation-list::-webkit-scrollbar-track,
.warning-list::-webkit-scrollbar-track,
.error-list::-webkit-scrollbar-track,
.results-content::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 3px;
}

.filter-overlay-content::-webkit-scrollbar-thumb,
.confirmation-list::-webkit-scrollbar-thumb,
.warning-list::-webkit-scrollbar-thumb,
.error-list::-webkit-scrollbar-thumb,
.results-content::-webkit-scrollbar-thumb {
  background: rgba(79, 195, 247, 0.3);
  border-radius: 3px;
}

.filter-overlay-content::-webkit-scrollbar-thumb:hover,
.confirmation-list::-webkit-scrollbar-thumb:hover,
.warning-list::-webkit-scrollbar-thumb:hover,
.error-list::-webkit-scrollbar-thumb:hover,
.results-content::-webkit-scrollbar-thumb:hover {
  background: rgba(79, 195, 247, 0.5);
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .filter-overlay-content {
    width: 95%;
    padding: 20px;
  }

  .entry-fields {
    grid-template-columns: repeat(2, 1fr);
  }

  .carousel-navigation {
    flex-wrap: wrap;
    gap: 12px;
  }

  .step-name {
    font-size: 9px;
  }
}

@media (max-width: 480px) {
  .entry-fields {
    grid-template-columns: 1fr;
  }

  .filter-actions {
    flex-direction: column;
  }

  .action-button {
    width: 100%;
  }
}
</style>